import { sortConversations, type Platform } from '@sd/client-core';
import type { ConversationSummary } from '@sd/contracts';
import type { InstallerDownload, UpdateInfo } from '@sd/contracts/ipc';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatBytes, formatListTime } from '../format';
import { Icon } from '../icons/Icon';
import { companyShortName, serverSlotOf } from '../ui/Avatar';
import { Button, IconButton } from '../ui/controls';
import { useConfirm, useToast } from '../ui/overlays';
import { ConnectionPill, ConversationAvatar } from './bits';
import { useAppEnv, useClient, useClientState, useTick } from './context';
import { conversationLabel, conversationName, previewText } from './copy';
import { ListHints } from './InstallHint';
import { useShell } from './shell';

type Filter = 'all' | 'unread' | 'group' | 'dm';
const FILTERS: [Filter, string][] = [
  ['all', '전체'],
  ['unread', '안 읽음'],
  ['group', '그룹'],
  ['dm', '1:1'],
];

/** Conversation list pane (design `aside.list`). */
export function ConversationList() {
  const env = useAppEnv();
  const shell = useShell();
  useTick();
  const conversations = useClientState((s) => s.conversations);
  const drafts = useClientState((s) => s.drafts);
  const pending = useClientState((s) => s.pending);
  const people = useClientState((s) => s.people);
  const meId = useClientState((s) => s.me?.id ?? '');
  const activeId = useClientState((s) => s.activeConversationId);
  // Without mail an invitation is a link someone hands over, not an email (ADR-0007).
  const inviteLabel = useClientState((s) => (s.serverInfo?.mail.status === 'not_configured' ? '직원 초대' : '이메일로 초대'));
  const client = useClient();
  // A finger on a row starts loading its conversation, before the tap ends and opens it.
  const prefetch = useCallback((conversationId: string) => void client.prefetch([conversationId]), [client]);
  const update = env.update;
  const webUpdate = webUpdateVersion(env.platform.kind, env.appInfo.appVersion, useClientState((s) => s.serverInfo?.serverVersion));
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const server = env.servers.find((s) => s.serverId === env.activeServerId);

  /** Newest unconfirmed send per conversation: the list shows what the user last wrote. */
  const latestPending = useMemo(() => {
    const map = new Map<string, (typeof pending)[string]>();
    for (const p of Object.values(pending)) {
      const current = map.get(p.conversationId);
      if (!current || p.createdAt > current.createdAt) map.set(p.conversationId, p);
    }
    return map;
  }, [pending]);

  const lookup = (id: string | null | undefined) => (id ? people[id] : undefined);
  const visible = useMemo(() => {
    const query = q.trim();
    return sortConversations(Object.values(conversations)).filter((c) => {
      if (query && !conversationName(c).includes(query)) return false;
      if (filter === 'unread') return c.unreadCount > 0;
      if (filter === 'group') return c.kind === 'group';
      if (filter === 'dm') return c.kind === 'direct';
      return true;
    });
  }, [conversations, filter, q]);
  // Pins keep a stable, name-based order so they do not jump around on every message.
  const pins = visible.filter((c) => c.my.pinned).sort((a, b) => conversationName(a).localeCompare(conversationName(b), 'ko'));
  const rest = visible.filter((c) => !c.my.pinned);

  /** The preview line, as plain values (a row redraws only when they change), and as said in the conversation's label. */
  const preview = (c: ConversationSummary): Line => {
    const readOnly = c.readOnlyReason === 'peer_deactivated' ? '비활성화된 계정 · 읽기 전용' : c.my.state === 'left' ? '나간 대화 · 읽기 전용' : c.readOnly ? '읽기 전용' : null;
    if (readOnly) return { kind: 'readonly', text: readOnly, label: readOnly };
    const draft = drafts[c.id]?.trim();
    if (draft && c.id !== activeId) return { kind: 'draft', text: draft, label: `임시저장: ${draft}` };
    const own = latestPending.get(c.id);
    const newer = own && (!c.lastMessage || own.createdAt >= c.lastMessage.createdAt);
    const text = newer ? own.text || (own.attachments[0] ? `[파일] ${own.attachments[0].name}` : '') : previewText(c, c.lastMessage, lookup, meId);
    if (own?.state === 'failed') return { kind: 'failed', text: own.text || text, label: `전송 실패 · ${own.text || text}` };
    return { kind: 'text', text, label: text };
  };

  return (
    <aside className="list pane glass" aria-label="대화 목록">
      <div className="list-head">
        <div className="lh-row top">
          <button type="button" className="co-chip" onClick={() => env.openModal({ kind: 'servers' })} aria-label="회사 서버 전환">
            {server ? <span className={`srv ${serverSlotOf(server.serverId)}`}>{companyShortName(server.companyName)}</span> : null}
            <span className="co-name ell">{server?.companyName}</span>
            <Icon name="down" size="sm" />
          </button>
          <span style={{ flex: 1 }} />
          <ConnectionPill compact />
        </div>
        <div className="lh-row">
          <h1 className="list-title">대화</h1>
          <IconButton icon="userplus" glassy label={inviteLabel} tip={inviteLabel} tipPos="below" onClick={() => env.openModal({ kind: 'invite' })} />
          <IconButton icon="compose" glassy label="새 대화" tip="새 대화" tipPos="left" onClick={() => env.openModal({ kind: 'people', mode: 'new' })} />
        </div>
        <label className="search-pill">
          <Icon name="search" size="sm" />
          <input type="search" placeholder="대화 이름 검색" aria-label="대화 이름 검색" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="chips" role="group" aria-label="대화 필터">
          {FILTERS.map(([key, name]) => (
            <button key={key} type="button" className={`chip${filter === key ? ' on' : ''}`} aria-pressed={filter === key} onClick={() => setFilter(key)}>
              {name}
            </button>
          ))}
        </div>
      </div>
      <div className="list-scroll scroll">
        <ListHints />
        {!visible.length ? <div className="empty-note">{Object.keys(conversations).length ? '조건에 맞는 대화가 없어요' : '아직 대화가 없어요. 새 대화를 시작해 보세요'}</div> : null}
        {pins.length ? (
          <>
            <div className="sec-h">
              <Icon name="pin" size="xs" />
              고정됨
            </div>
            <div className="pinned-row">
              {pins.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`pin${c.id === activeId ? ' active' : ''}`}
                  onClick={() => shell.openConversation(c.id)}
                  aria-label={conversationLabel(c, preview(c).label, formatListTime(c.lastActivityAt, env.now()))}
                  aria-current={c.id === activeId ? 'true' : undefined}
                >
                  <span className="ring">
                    <ConversationAvatar conversation={c} />
                  </span>
                  <span className="pn ell">{conversationName(c)}</span>
                  {c.unreadCount ? <span className={`badge${c.my.muted ? ' muted' : ''}`}>{c.unreadCount}</span> : null}
                </button>
              ))}
            </div>
          </>
        ) : null}
        {rest.length ? <div className="sec-h">최근 대화</div> : null}
        {rest.map((c) => (
          <ConversationRow
            key={c.id}
            c={c}
            active={c.id === activeId}
            {...preview(c)}
            time={formatListTime(c.lastActivityAt, env.now())}
            onOpen={shell.openConversation}
            onPress={prefetch}
          />
        ))}
      </div>
      {update ? <UpdateCard update={update} /> : webUpdate ? <WebUpdateCard version={webUpdate} /> : null}
      <nav className="tabbar" aria-label="하단 메뉴">
        <button type="button" className="on" aria-current="page">
          <Icon name="chat" />
          <span className="tl">대화</span>
        </button>
        <button type="button" onClick={() => env.openModal({ kind: 'people', mode: 'new' })}>
          <Icon name="users" />
          <span className="tl">사람</span>
        </button>
        <button type="button" onClick={() => env.openModal({ kind: 'search' })}>
          <Icon name="search" />
          <span className="tl">검색</span>
        </button>
        <button type="button" onClick={() => env.openModal({ kind: 'settings' })}>
          <Icon name="gear" />
          <span className="tl">설정</span>
        </button>
      </nav>
    </aside>
  );
}

/**
 * The company server now runs another version than this page was loaded from (web): it was updated while the page
 * stayed open, and a reload brings the matching web app. The server info is read again at every reconnect.
 */
function webUpdateVersion(kind: Platform['kind'], appVersion: string, serverVersion: string | undefined): string | null {
  return kind === 'web' && appVersion && serverVersion && serverVersion !== appVersion ? serverVersion : null;
}

/** Reloads the page, asking first when something unsent would go: the web app keeps drafts, queued sends and picked files only in memory. */
function useWebReload() {
  const client = useClient();
  const confirm = useConfirm();
  return async () => {
    const s = client.state;
    const unsent =
      Object.keys(s.pending).length + Object.values(s.drafts).filter((text) => text.trim()).length + Object.values(s.trays).reduce((n, items) => n + items.length, 0);
    if (
      unsent &&
      !(await confirm({
        title: '새로고침할까요?',
        body: '보내지 않은 메시지, 임시저장한 글, 첨부하려던 파일은 이 브라우저에 저장되지 않아 새로고침하면 사라져요.',
        ok: '새로고침',
      }))
    )
      return;
    window.location.reload();
  };
}

/**
 * The web counterpart of the update card: one reload. A phone showing a conversation has the list (and the card) out of
 * sight, so there it is announced once as a toast with the same button (it stays until used or closed).
 */
function WebUpdateCard({ version }: { version: string }) {
  const toast = useToast();
  const shell = useShell();
  const reload = useWebReload();
  const announced = useRef<string | null>(null);
  const hidden = shell.single && shell.view !== 'list';
  useEffect(() => {
    if (!hidden || announced.current === version) return;
    announced.current = version;
    toast(`새 버전 ${version}이 나왔어요. 새로고침하면 바로 바뀌어요`, { icon: 'sparkle', action: '새로고침', onAction: () => void reload() });
  }, [hidden, version, toast, reload]);
  return (
    <div className="update-card">
      <div className="uc-ic">
        <Icon name="sparkle" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b>새 버전 {version}</b>
        <span>새로고침하면 바로 바뀌어요.</span>
      </div>
      <Button size="sm" variant="blue" onClick={() => void reload()}>
        새로고침
      </Button>
    </div>
  );
}

type Line = { kind: 'readonly' | 'draft' | 'failed' | 'text'; text: string; label: string };

/**
 * One conversation of the list. memo with plain props: the list is drawn again at every new message, unread count or
 * reconnect, and with many conversations each row was too (a message in the open one redrew 200 rows twice).
 */
const ConversationRow = memo(function ConversationRow({
  c,
  active,
  kind,
  text,
  label,
  time,
  onOpen,
  onPress,
}: Line & { c: ConversationSummary; active: boolean; time: string; onOpen(conversationId: string): void; onPress(conversationId: string): void }) {
  const inactive = c.readOnly;
  return (
    <button
      type="button"
      className={`conv${active ? ' active' : ''}${inactive ? ' inactive' : ''}`}
      onClick={() => onOpen(c.id)}
      onPointerDown={() => onPress(c.id)}
      aria-label={conversationLabel(c, label, time)}
      aria-current={active ? 'true' : undefined}
    >
      <ConversationAvatar conversation={c} />
      <span className="cv-main">
        <span className="cv-top">
          <span className="cv-name ell">{conversationName(c)}</span>
          {c.kind === 'group' ? <span className="cv-cnt">{c.memberCount}</span> : null}
          {c.my.muted ? (
            <span className="cv-ico" title="음소거">
              <Icon name="belloff" />
            </span>
          ) : null}
          {inactive ? (
            <span className="cv-ico">
              <Icon name="lock" />
            </span>
          ) : null}
          <span className="cv-time">{time}</span>
        </span>
        <span className="cv-bot">
          <span className="cv-prev ell">
            {kind === 'readonly' ? (
              <span className="ro">{text}</span>
            ) : kind === 'draft' ? (
              <>
                <span className="draft">임시저장:</span> {text}
              </>
            ) : kind === 'failed' ? (
              <>
                <span className="fail">
                  <Icon name="alert" size="xs" /> 전송 실패
                </span>{' '}
                · {text}
              </>
            ) : (
              text
            )}
          </span>
          {c.unreadCount ? <span className={`badge${c.my.muted ? ' muted' : ''}`}>{c.unreadCount}</span> : null}
        </span>
      </span>
    </button>
  );
});

const INSTALLER_FAILED: Record<NonNullable<InstallerDownload['failure']>, string> = {
  mismatch: '받은 파일이 서명된 정보와 달라서 지웠어요. 다시 받거나 다운로드 페이지에서 받아 주세요.',
  network: '설치 파일을 받지 못했어요. 네트워크를 확인하고 다시 받아 주세요.',
  http: '배포 주소에서 설치 파일을 받지 못했어요.',
  disk: '다운로드 폴더에 저장하지 못했어요. 남은 공간과 권한을 확인해 주세요.',
};

/**
 * The update notice (R39): restart into the staged update (Windows), the installer the app
 * downloads and checks itself (Linux, macOS), or the provider's download page.
 */
function UpdateCard({ update }: { update: UpdateInfo }) {
  const env = useAppEnv();
  const toast = useToast();
  const updates = env.platform.updates;
  const installer = update.installer;
  const title = (
    <b>
      {update.required ? '필수 업데이트' : '새 버전'} {update.version}
    </b>
  );
  // Without a usable key store (Linux without a keyring) unsent messages live only in this window.
  const kept = env.appInfo.secureStorage === 'os' ? '작성 중인 메시지는 보존돼요.' : '이 PC에서는 보내지 않은 메시지가 보존되지 않아요.';
  /** `failed`: the toast when nothing happened; null when main says why itself (the card changes, or `update.openRefused`). */
  const install = (failed: string | null) =>
    void env.installUpdate().catch(() => {
      if (failed) toast(failed);
    });
  const page = () => void updates?.openDownloadPage().catch(() => toast('다운로드 페이지를 열지 못했어요'));

  if (!installer || update.install === 'restart') {
    const restart = update.install === 'restart';
    return (
      <div className="update-card">
        <div className="uc-ic">
          <Icon name="sparkle" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {title}
          <span>{restart ? `재시작하면 업데이트돼요. ${kept}` : '다운로드 페이지에서 새 버전을 받아 설치해 주세요.'}</span>
        </div>
        <Button size="sm" variant="blue" onClick={() => install(restart ? '업데이트를 시작하지 못했어요. 앱을 다시 열어 주세요' : '다운로드 페이지를 열지 못했어요')}>
          {restart ? '재시작' : '다운로드'}
        </Button>
      </div>
    );
  }

  const download = () => void updates?.download?.().catch(() => toast('설치 파일을 받지 못했어요'));
  const pct = installer.size ? Math.min(100, Math.round((installer.receivedBytes / installer.size) * 100)) : 0;
  // Two buttons in fixed places from state to state, so keyboard focus stays on the one pressed.
  let text: string;
  let primary: { label: string; run(): void; quiet?: boolean };
  let secondary: { label: string; run(): void } | null = null;
  if (installer.state === 'downloading') {
    text = '설치 파일을 받는 중이에요';
    primary = { label: '취소', quiet: true, run: () => void updates?.cancelDownload?.().catch(() => {}) };
  } else if (installer.state === 'ready') {
    text = `설치 파일을 확인했어요. 설치 프로그램을 열면 앱이 종료되고, ${kept}`;
    // Nothing handed over: the file went away or changed (the card says so), the offer was withdrawn
    // (the card goes), or the system has no program for it (`update.openRefused` says to open it by hand).
    primary = { label: '설치', run: () => install(null) };
    secondary = { label: '폴더에서 보기', run: () => void updates?.showInstaller?.().catch(() => {}) };
  } else if (installer.state === 'failed') {
    text = INSTALLER_FAILED[installer.failure ?? 'network'];
    primary = { label: '다시 받기', run: download };
    secondary = { label: '다운로드 페이지', run: page };
  } else {
    text = `설치 파일을 받아 서명된 정보와 같은지 확인해요 · ${formatBytes(installer.size)}`;
    primary = { label: '다운로드', run: download };
  }
  return (
    <div className="update-card">
      <div className="uc-ic">
        <Icon name="sparkle" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title}
        {/* Each new state is announced; the percentage is the progress bar's value, not read out at every step. */}
        <span aria-live="polite">{text}</span>
        {installer.state === 'downloading' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <div className="prog" role="progressbar" aria-label="설치 파일 다운로드" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} style={{ flex: 1 }}>
              <i style={{ width: `${pct}%` }} />
            </div>
            <span aria-hidden="true">{pct}%</span>
          </div>
        ) : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          <Button size="sm" variant={primary.quiet ? 'ghost' : 'blue'} onClick={primary.run}>
            {primary.label}
          </Button>
          {secondary ? (
            <Button size="sm" variant="ghost" onClick={secondary.run}>
              {secondary.label}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
